import type { FC, KeyboardEvent, MouseEvent, ReactElement } from 'react';
import { cloneElement, useContext } from 'react';
import PropTypes from 'prop-types';

import { DropdownContext } from './dropdown-context';

interface DropdownButtonProps {
  children: ReactElement;
}

export const DropdownTrigger: FC<DropdownButtonProps> = (props) => {
  const { children } = props;
  const { onTriggerEnter, onTriggerLeave, onTriggerToggle, onClose, open } =
    useContext(DropdownContext);

  return cloneElement(children, {
    onMouseEnter: (event: MouseEvent<HTMLElement>) => {
      children.props.onMouseEnter?.(event);
      onTriggerEnter(event);
    },
    onMouseLeave: (event: MouseEvent<HTMLElement>) => {
      children.props.onMouseLeave?.(event);
      onTriggerLeave(event);
    },
    // Keyboard equivalents of the hover interaction (WCAG 2.1.1). Enter/Space
    // (the focusable child button's native activation bubbles here) and
    // ArrowDown open the submenu; Escape closes it.
    onClick: (event: MouseEvent<HTMLElement>) => {
      children.props.onClick?.(event);
      onTriggerToggle(event);
    },
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      children.props.onKeyDown?.(event);
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (!open) onTriggerToggle(event as unknown as MouseEvent<HTMLElement>);
      } else if (event.key === 'Escape') {
        onClose();
      }
    },
    'aria-haspopup': 'menu',
    'aria-expanded': open,
  });
};

DropdownTrigger.propTypes = {
  children: PropTypes.element.isRequired as any,
};
